import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { InvoiceForm } from '../components/InvoiceForm';
import { useCreateInvoice, useInvoice, useUpdateInvoice } from '../queries';
import type { InvoiceInput } from '../types';

export function InvoiceFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const invoiceQuery = useInvoice(isEdit ? publicId : -1);
  const create = useCreateInvoice();
  const update = useUpdateInvoice(publicId);

  async function handleSubmit(values: InvoiceInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/finance/billing/${saved.public_id}`);
  }

  if (isEdit && invoiceQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const invoice = invoiceQuery.data;

  // Only a draft is editable (the server refuses the rest): a typed /edit URL
  // for an issued / paid / void invoice points back instead of showing a form
  // whose Status can't hold that value (BE ADR-103).
  if (isEdit && invoice && invoice.status !== 'draft') {
    return (
      <div className="max-w-xl space-y-3">
        <p role="status" className="text-muted-foreground text-sm">
          Only a draft invoice can be edited.
          {invoice.status === 'issued' ? ' Revert it to draft from its page to make changes.' : ''}
        </p>
        <Button variant="outline" onClick={() => navigate(`/finance/billing/${publicId}`)}>
          Back to the invoice
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/finance/billing/${publicId}` : '/finance/billing')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${invoice?.number ?? 'invoice'}` : 'New invoice'}
        </h1>
      </div>

      <InvoiceForm
        submitLabel={isEdit ? 'Save changes' : 'Create invoice'}
        currentProjects={invoice?.project_details ?? []}
        defaultValues={
          invoice
            ? {
                direction: invoice.direction,
                number: invoice.number,
                company: invoice.company ? String(invoice.company) : '',
                deal: invoice.deal ? String(invoice.deal) : '',
                projects: invoice.projects.map(String),
                counterparty_name: invoice.counterparty_name,
                status: invoice.status,
                currency: invoice.currency,
                issue_date: invoice.issue_date ?? '',
                due_date: invoice.due_date ?? '',
                period_start: invoice.period_start ?? '',
                period_end: invoice.period_end ?? '',
                notes: invoice.notes,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/finance/billing/${publicId}` : '/finance/billing')}
      />
    </div>
  );
}
